import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StapGridlines } from '@/pages/Overview/StapGridlines';
import { StapLabels } from '@/pages/Overview/StapLabels';
import {
  getValueColumnClass,
  isAchievableChart,
  normalizeEvaluation,
  rankingTitleKeys,
} from '@/pages/Overview/ratingsChart';
import type {
  EvaluationChartType,
  StudentDisciplineChart,
} from '@/types/ratings';
import { toPercentage } from '@/utils/text';
import { tv } from '@/utils/tv';

const MIN_FACTOR_FOR_PLACE_INSIDE_BAR = 0.12;
const AVERAGE_LINE_BACKGROUND =
  'repeating-linear-gradient(to bottom, var(--accent) 0 6px, transparent 6px 12px)';

const bar = tv({
  base: 'flex h-6 items-center rounded-r-[4px]',
  variants: {
    isCurrentStudent: {
      true: 'bg-accent px-2',
      false: 'bg-graph-hover',
    },
  },
});

const averageLine = tv({
  base: 'absolute w-0.5',
  variants: {
    isExtendedUp: {
      true: 'top-0 md:-top-3',
      false: 'top-0',
    },
    isExtendedDown: {
      true: '-bottom-3',
      false: 'bottom-0',
    },
    isHiddenOnMobile: {
      true: 'max-md:hidden',
    },
  },
});

interface OverlayProps {
  valueColClass: string | null;
  children: ReactNode;
}

const ChartOverlay: React.FC<OverlayProps> = ({ valueColClass, children }) => (
  <div aria-hidden className="pointer-events-none absolute inset-0 flex gap-2">
    <div className="relative flex-1">{children}</div>
    {valueColClass && <span className={`${valueColClass} shrink-0`} />}
  </div>
);

interface Props {
  chart: StudentDisciplineChart;
  type: EvaluationChartType;
  title?: string;
}

export const StudentRankingCard: React.FC<Props> = ({ chart, type, title }) => {
  const { t } = useTranslation();

  const { chartValues, averageClassEvaluationTotal } = chart;
  const isAchievable = isAchievableChart(type);
  const valueColClass = isAchievable ? null : getValueColumnClass(type);
  const currentStudentIndex = chartValues.findIndex(
    (entry) => entry.isCurrentStudent,
  );
  const place = currentStudentIndex + 1;
  const isCurrentStudentFirst = currentStudentIndex === 0;
  const isSingleRow = chartValues.length === 1;
  const averageLabel = averageClassEvaluationTotal?.stringValue;
  const averageLeft = toPercentage(
    normalizeEvaluation(averageClassEvaluationTotal?.value ?? 0, type) * 100,
  );

  const rows = chartValues.map((entry, index) => {
    const { evaluation, isCurrentStudent } = entry;
    const factor = normalizeEvaluation(evaluation.value, type);
    const width = toPercentage(factor * 100);
    const isPlaceInsideBar = factor >= MIN_FACTOR_FOR_PLACE_INSIDE_BAR;
    const placeLabel = t('overview.ratings.ranking.place', { place });
    const isFirst = index === 0;
    const isLast = index === chartValues.length - 1;
    const hasGapAbove = isCurrentStudent && !isFirst;
    const hasGapBelow = isCurrentStudent && !isLast;
    const hasValue =
      Boolean(valueColClass) && (isCurrentStudent || isFirst || isLast);
    const isPlaceOutsideBar = isCurrentStudent && !isPlaceInsideBar;

    return (
      <div
        key={index}
        className={`flex items-center gap-2 ${isCurrentStudent ? 'relative z-10' : ''}`}
      >
        <div
          className={`relative min-w-0 flex-1 ${hasGapAbove ? 'pt-1' : ''} ${hasGapBelow ? 'pb-1' : ''}`}
        >
          <div style={{ width }} className={bar({ isCurrentStudent })}>
            {isCurrentStudent && isPlaceInsideBar && (
              <span className="text-caption-13 truncate text-accent-foreground md:text-subhead-14">
                {placeLabel}
              </span>
            )}
          </div>
          {(isPlaceOutsideBar || hasValue) && (
            <span
              style={{ left: `calc(${width} + 8px)` }}
              className={`absolute inset-y-0 items-center gap-2 whitespace-nowrap text-foreground ${isPlaceOutsideBar ? 'flex' : 'hidden md:flex'}`}
            >
              {isPlaceOutsideBar && (
                <span className="text-caption-13 md:text-subhead-14">
                  {placeLabel}
                </span>
              )}
              {hasValue && (
                <span className="hidden text-subhead-14 md:inline">
                  {evaluation.stringValue}
                </span>
              )}
            </span>
          )}
        </div>
        {valueColClass && (
          <span
            className={`${valueColClass} shrink-0 text-right md:invisible ${isCurrentStudent ? 'text-caption-13 text-foreground' : 'text-body-13 text-muted-foreground'}`}
          >
            {hasValue && evaluation.stringValue}
          </span>
        )}
      </div>
    );
  });

  const classAverageLegend = (
    <div className="flex items-center gap-1">
      <span className="h-0 w-6 shrink-0 border-t-2 border-dashed border-accent" />
      <span className="text-body-13 text-subtle-foreground">
        {isAchievable
          ? t('overview.ratings.ranking.classAverage')
          : t('overview.ratings.ranking.classAverageValue')}
      </span>
      {!isAchievable && (
        <span className="text-subhead-14 text-foreground">{averageLabel}</span>
      )}
    </div>
  );

  const placeOfTotal = (
    <span className="text-body-13 text-subtle-foreground md:text-subhead-14 md:text-foreground">
      {t('overview.ratings.ranking.placeOfTotal', {
        place,
        total: chartValues.length,
      })}
    </span>
  );

  const averageLineOverlay = (
    <ChartOverlay valueColClass={valueColClass}>
      <span
        style={{ left: averageLeft, backgroundImage: AVERAGE_LINE_BACKGROUND }}
        className={averageLine({
          isExtendedUp: isCurrentStudentFirst,
          isExtendedDown: isSingleRow,
          isHiddenOnMobile: isSingleRow,
        })}
      />
      {!isAchievable && (
        <span
          style={{ left: averageLeft }}
          className={`absolute bottom-full hidden -translate-x-1/2 text-subhead-14 text-accent md:block ${isCurrentStudentFirst ? 'mb-[18px]' : 'mb-1.5'}`}
        >
          {averageLabel}
        </span>
      )}
    </ChartOverlay>
  );

  return (
    <div className="flex flex-col rounded-xl bg-popover p-4 shadow-card md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-callout-16 text-foreground">
          {title ?? t(rankingTitleKeys[type])}
        </h3>
        <div className="hidden items-center gap-4 md:flex">
          {classAverageLegend}
          {placeOfTotal}
        </div>
      </div>

      <div
        className={`relative z-0 mt-6 flex flex-col ${isCurrentStudentFirst ? 'md:mt-10' : 'md:mt-8'}`}
      >
        {isAchievable && (
          <ChartOverlay valueColClass={valueColClass}>
            <StapGridlines />
          </ChartOverlay>
        )}

        {rows}
        {isAchievable && <div className="h-px bg-border-strong/8" />}
        {averageClassEvaluationTotal && averageLineOverlay}
      </div>

      {isAchievable && (
        <div className="relative mt-3 h-5 text-body-14 text-subtle-foreground">
          <StapLabels />
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-2 md:hidden">
        {placeOfTotal}
        {classAverageLegend}
      </div>
    </div>
  );
};
